// --- Our Modules --------------------------------------------------------------------------------
import type { TyBranchDetailPrice, TyProduct } from "@/types";
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    product: TyProduct;
    branchEntry?: TyBranchDetailPrice | null;
    isInactiveAtBranch: boolean;
    isHiddenAtBranch: boolean;
    canEdit?: boolean;
    onToggleInactive?: () => void;
    onToggleHidden?: () => void;
    togglingInactive?: boolean;
    togglingHidden?: boolean;
    onMore?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

// --- Logic --------------------------------------------------------------------------------------

export default function BranchProductCard({
    product,
    branchEntry,
    isInactiveAtBranch,
    isHiddenAtBranch,
    canEdit = true,
    onToggleInactive,
    onToggleHidden,
    togglingInactive,
    togglingHidden,
    onMore,
}: Props) {
    const t = useTranslation();

    const isGloballyInactive = !product.active;
    const isDimmed = isGloballyInactive || isInactiveAtBranch;

    // Branch-specific price display: filter hidden prices, apply amount overrides
    const branchPriceOverrides = branchEntry?.prices ?? [];
    const branchHiddenPriceIds = branchEntry?.hiddenPrices ?? [];
    const visiblePrices = product.prices
        .filter((p) => !branchHiddenPriceIds.includes(p._id))
        .map((p) => {
            const override = branchPriceOverrides.find((o) => o._id === p._id);
            return {
                ...p,
                amount: override !== undefined ? override.amount : p.amount,
                showLabel: override?.showLabel !== undefined ? override.showLabel : p.showLabel,
            };
        });

    // Branch-specific discount: branch override wins if set, otherwise fall back to product discount
    const hasBranchDiscountOverride = branchEntry !== null && branchEntry !== undefined && branchEntry.discount !== null;
    const effectiveDiscount = hasBranchDiscountOverride
        ? branchEntry!.discount!.active
            ? branchEntry!.discount
            : null
        : product.discount?.active
          ? product.discount
          : null;

    const discountLabel = effectiveDiscount
        ? effectiveDiscount.type === "percentage"
            ? `${t.menu.discount} ${effectiveDiscount.amount}%`
            : `${t.menu.discount} ${effectiveDiscount.amount}`
        : null;

    const hasChanges =
        isInactiveAtBranch ||
        isHiddenAtBranch ||
        (branchEntry !== null &&
            branchEntry !== undefined &&
            (branchEntry.prices.length > 0 || branchEntry.hiddenPrices.length > 0 || branchEntry.discount !== null));

    return (
        <div className={`branch-product-card${isDimmed ? " branch-product-card--inactive" : ""}${!canEdit ? " branch-product-card--view-only" : ""}`}>
            <div className="branch-product-card-inner">
                <div className="branch-product-card-image">
                    {product.pictures[0] ? (
                        <img src={product.pictures[0]} alt={product.name} className="branch-product-card-image-img" />
                    ) : (
                        <div className="branch-product-card-image-placeholder">
                            <img src="/icons/category-icon.svg" alt="" width={28} height={28} />
                        </div>
                    )}
                </div>

                <div className="branch-product-card-info">
                    <p className="branch-product-card-name">{product.name}</p>
                    <p className="branch-product-card-description">{product.description}</p>
                    <div className="branch-product-card-prices">
                        {visiblePrices.map((price) => (
                            <div key={price._id} className="branch-product-card-price-chip">
                                {price.showLabel && <span className="branch-product-card-price-label">{price.label}:</span>}
                                <span className="branch-product-card-price-amount">{price.amount}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {discountLabel && (
                    <div className="branch-product-card-ribbon" aria-label={discountLabel}>
                        <div className="branch-product-card-ribbon-band">
                            <span className="branch-product-card-ribbon-text">{discountLabel}</span>
                        </div>
                    </div>
                )}

                {hasChanges && (
                    <div className="branch-product-card-ribbon branch-product-card-ribbon--changed">
                        <div className="branch-product-card-ribbon-band branch-product-card-ribbon-band--changed">
                            <span className="branch-product-card-ribbon-text">{t.branchMenu.changedCountLabel}</span>
                        </div>
                    </div>
                )}
            </div>

            {canEdit && (
                <div className="branch-product-card-footer">
                    <button
                        type="button"
                        className={`branch-product-card-toggle-pill${togglingInactive ? " branch-product-card-toggle-pill--loading" : ""}`}
                        onClick={onToggleInactive}
                        disabled={togglingInactive || !onToggleInactive}
                    >
                        <span className="branch-product-card-toggle-label">{t.branchMenu.activeHereLabel}</span>
                        <div className={`branch-product-card-toggle${!isInactiveAtBranch ? " branch-product-card-toggle--on" : ""}`} />
                    </button>
                    <button
                        type="button"
                        className={`branch-product-card-toggle-pill${togglingHidden ? " branch-product-card-toggle-pill--loading" : ""}`}
                        onClick={onToggleHidden}
                        disabled={togglingHidden || !onToggleHidden}
                    >
                        <span className="branch-product-card-toggle-label">{t.branchMenu.hiddenHereLabel}</span>
                        <div className={`branch-product-card-toggle${isHiddenAtBranch ? " branch-product-card-toggle--on" : ""}`} />
                    </button>
                    <button type="button" className="branch-product-card-more-btn" onClick={onMore} disabled={!onMore}>
                        <img src="/icons/more-icon.svg" alt="" width={16} height={16} />
                    </button>
                </div>
            )}
        </div>
    );
}
